import { inject, Injectable, INJECTOR, signal } from '@angular/core';
import { SOFTLINE_MDE_INVENTORY_REPOSITORY } from '../repositories/inventory.repository';
import { Inventory } from '../types/inventory';
import { handleRequestError } from '@softline/application';
import { InventoryStorage } from '../types/inventory-storage';

const DRAFT_KEY = 'softline.mde.inventory.draft';

@Injectable()
export class InventoryService {
  private readonly repository = inject(SOFTLINE_MDE_INVENTORY_REPOSITORY);
  readonly #injector = inject(INJECTOR);

  readonly #value = signal<Inventory | null>(null);
  readonly #loaded = signal<boolean>(false);

  readonly value = this.#value.asReadonly();
  readonly loaded = this.#loaded.asReadonly();

  async loadExisting(): Promise<Inventory | null> {
    const result = await this.repository.loadExisting();
    this.set(result);
    if (result) this.#clearDraft();
    return result;
  }

  /** Kept for reloads until the first position creates the Inventur on the server. */
  startNew(value: Partial<Inventory>): void {
    this.set(value);
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(value));
    } catch {
      return;
    }
  }

  restoreDraft(): Inventory | null {
    try {
      const draft = JSON.parse(
        sessionStorage.getItem(DRAFT_KEY) ?? 'null'
      ) as Inventory | null;
      if (!draft?.lager) return null;
      this.set(draft);
      return draft;
    } catch {
      return null;
    }
  }

  async loadDefaultLager(): Promise<InventoryStorage | null> {
    return this.repository.loadDefaultLager();
  }

  set(value: Partial<Inventory> | null): void {
    this.#value.set(value as Inventory);
    this.#loaded.set(true);
  }

  async cancel(): Promise<boolean> {
    try {
      await this.repository.cancel();
      this.#loaded.set(false);
      return true;
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
      return false;
    }
  }

  async finish(params: {
    schwundlisteDrucken: boolean;
    differenz: number;
  }): Promise<void> {
    await this.repository.finish(params);
  }

  reset() {
    this.#value.set(null);
    this.#loaded.set(false);
    this.#clearDraft();
  }

  #clearDraft(): void {
    try {
      sessionStorage.removeItem(DRAFT_KEY);
    } catch {
      return;
    }
  }
}
